import { Checkbox, DatePicker, Flex, Form, TimePicker } from "antd"
import { endDateValidate, endTimeValidate, startDateValidate, startTimeValidate } from "pagesV2/pro/Calendar/utils"
import { useTranslation } from "react-i18next"
import { spacings } from "themes/variables"
import { Text } from "ui/Typography"
import dayjs from "dayjs"
import { useEffect, useState } from "react"
import PropTypes from "prop-types"

const DateRange = ({ form }) => {
  const { t } = useTranslation()
  const [isPartOfDay, setIsPartOfDay] = useState(false)

  const fromDate = Form.useWatch("dateRangeFromDate", form)
  const toDate = Form.useWatch("dateRangeToDate", form)
  const isSameDay = fromDate && toDate && fromDate.isSame(toDate, "day")

  useEffect(() => {
    if (!isSameDay && isPartOfDay) {
      setIsPartOfDay(false)
      form.setFieldsValue({ dateRangeFromTime: null, dateRangeToTime: null })
    }
  }, [isSameDay]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Flex vertical gap={spacings.M}>
      <Text fw={500}>{t("calendar.schedule_time_off.select_start_and_end_dates")}</Text>
      <Flex vertical>
        <Flex gap={spacings.M}>
          <Form.Item
            name="dateRangeFromDate"
            className="w-full"
            rules={[
              { required: true, message: t("calendar.schedule_time_off.start_date_is_required") },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  return startDateValidate(t, getFieldValue("dateRangeToDate"), value)
                }
              })
            ]}
          >
            <DatePicker
              className="w-full"
              format="MM/DD/YYYY"
              placeholder="MM/DD/YYYY"
              disabledDate={(current) => current?.isBefore(dayjs()?.startOf("day"))}
            />
          </Form.Item>
          <Form.Item
            name="dateRangeToDate"
            className="w-full"
            rules={[
              { required: true, message: t("calendar.schedule_time_off.end_date_is_required") },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  return endDateValidate(t, getFieldValue("dateRangeFromDate"), value)
                }
              })
            ]}
          >
            <DatePicker
              className="w-full"
              format="MM/DD/YYYY"
              placeholder="MM/DD/YYYY"
              disabledDate={(current) => current?.isBefore(dayjs()?.startOf("day"))}
            />
          </Form.Item>
        </Flex>

        {isSameDay && (
          <div className="py-xxs mb-xl">
            <Checkbox
              checked={isPartOfDay}
              onChange={(e) => {
                setIsPartOfDay(e.target.checked)
                if (!e.target.checked) {
                  form.setFieldsValue({ dateRangeFromTime: null, dateRangeToTime: null })
                }
              }}
            >
              {t("crewMember.scheduledTimeOff.fields.partOfDay")}
            </Checkbox>
          </div>
        )}

        {isPartOfDay && (
          <Flex gap={spacings.M}>
            <Form.Item
              name="dateRangeFromTime"
              className="w-full"
              rules={[
                { required: true, message: t("calendar.schedule_time_off.start_time_is_required") },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    return startTimeValidate(t, getFieldValue("dateRangeToTime"), value)
                  }
                })
              ]}
            >
              <TimePicker use12Hours format="hh:mm a" needConfirm={false} className="w-full" placeholder="HH:MM:SS" />
            </Form.Item>
            <Form.Item
              name="dateRangeToTime"
              className="w-full"
              rules={[
                { required: true, message: t("calendar.schedule_time_off.end_time_is_required") },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    return endTimeValidate(t, getFieldValue("dateRangeFromTime"), value)
                  }
                })
              ]}
            >
              <TimePicker use12Hours format="hh:mm a" needConfirm={false} className="w-full" placeholder="HH:MM:SS" />
            </Form.Item>
          </Flex>
        )}
      </Flex>
    </Flex>
  )
}

DateRange.propTypes = {
  form: PropTypes.object.isRequired
}

export default DateRange
